import * as status from '../status/actions'
import {
  LOAD_FACILITY,
  RESET_FACILITY,
  ADD_OBJECT,
  UPDATE_OBJECT,
  UPDATE_OBJECTS,
  UPDATE_OBJECTS_PASSIVELY,
  UPDATE_WALL,
  UPDATE_SEGMENTS,
  DELETE_OBJECTS,
  DUPLICATE_OBJECTS,
  DUPLICATE_GRID,
  UPDATE_ROOF,
  UPDATE_ROOF_STRUCTURE,
  UPDATE_ROOF_SECTION,
  ADD_MOUNTING_BEAM,
  REMOVE_MOUNTING_BEAM,
  UPDATE_MOUNTING_BEAM,
  ADD_COLUMN_LINE,
  REMOVE_COLUMN_LINE,
  UPDATE_COLUMN_LINE,
  REVERT_ROOF_SECTIONS,
  ADD_ELEVATION_POINT,
  UPDATE_ELEVATION_POINT,
  ADD_ELEVATION_LINE,
  UPDATE_ELEVATION_LINE,
  ADD_DOOR,
  UPDATE_DOOR,
  DISTRIBUTE_DOORS,
  ADD_UTILITY_BOX,
  UPDATE_UTILITY_BOX,
  ADD_PRODUCT,
  DISTRIBUTE_PRODUCTS,
  UPDATE_PRODUCT,
  ADD_OBSTRUCTION,
  DISTRIBUTE_OBSTRUCTIONS,
  UPDATE_OBSTRUCTION,
  ADD_COMFORT_ZONE,
  UPDATE_COMFORT_ZONE,
  ADD_DIMENSION,
  UPDATE_DIMENSION,
  ADD_CEILING,
  UPDATE_CEILING,
  DELETE_CEILING,
  REQUEST_AIRFLOW,
  RECEIVE_AIRFLOW,
  UPDATE_AIRFLOW_STATUS,
  SET_AIRFLOW_LAYER,
  ADD_BACKGROUND_IMAGE,
  DELETE_BACKGROUND_IMAGE,
  UPDATE_BACKGROUND_IMAGE,
  ADD_METADATA_IMAGE,
  REQUEST_HEAT_MAP,
  RECEIVE_HEAT_MAP,
  UPDATE_HEAT_MAP_STATUS,
  SET_HEAT_MAP_LAYER,
} from './action_types'
export * from './action_types'
import { patchupImport } from '../../lib/segment-patchup'
import type { AppDispatch, AppGetState, RootState } from '~/store'
import {
  Airflow,
  BackgroundImage,
  Ceiling,
  ComfortZone,
  DenormalizedWall,
  Dimension,
  Door,
  ElevationLine,
  ElevationPoint,
  Obstruction,
  PartialObject,
  Product,
  Roof,
  RoofSection,
  StateObject,
  UtilityBox,
  Wall,
  WallSegment,
} from '~/store/objects/types'
import { patchupBackgroundImage } from '~/lib/backgroundImage-patchup'
import { guid } from '~/lib/utils'
import { isAction, PayloadAction, PayloadActionCreator, PrepareAction } from '@reduxjs/toolkit'
import { Vector3, type Vector3Like, Line3 } from 'three'
import { updateCeilings } from './ceilingUpdater'
import { updateRoofs } from './roofUpdater'
import { getMountingStructurePositions } from '~/components/DrawingCanvas/MountingStructures/util'
import { getBeamOrientation, getOrthoOrientation } from '~/components/Panels/SelectedRoofSectionPanel/util'
import { patchupRoofSections } from '~/lib/roofSections-patchup'
import LayerKeys from '~/config/layerKeys'
import { Edge, isPointInPolygon } from '~/utils/pointInPolygon'
import { modelToUI, vectorUIToModel } from '~/components/DrawingCanvas/util/units'

export function createAction<P = void, T extends string = string>(type: T): PayloadActionCreator<P, T>
export function createAction<PA extends PrepareAction<any>, T extends string = string>(
  type: T,
  prepareAction: PA
): PayloadActionCreator<ReturnType<PA>['payload'], T, PA>
export function createAction(type: string, prepareAction?: any): any {
  function actionCreator(...args: any[]) {
    if (prepareAction) {
      const prepared = prepareAction(...args)
      return { type, ...prepared }
    }
    return { type, payload: args[0] }
  }
  actionCreator.toString = () => `${type}`
  actionCreator.type = type
  actionCreator.match = (action: unknown): action is PayloadAction => isAction(action) && action.type === type
  return actionCreator
}

const POINT_ON_WALL_THRESHOLD = 0.0000001

export function facilityBoundsChecker(stateSegments: Record<string, WallSegment>) {
  const segments = Object.values(stateSegments).filter(({ layerKey }) => layerKey === LayerKeys.EXTERIOR_WALLS)
  const edges: Edge[] = segments.map(({ startPoint, endPoint }) => ({
    p1: [startPoint.x, startPoint.y],
    p2: [endPoint.x, endPoint.y],
  }))
  const input = new Vector3()
  const target = new Vector3()
  const line = new Line3()

  return ({ x, y }: Vector3Like) => {
    input.set(x, y, 0)

    const onWall = segments.find(({ startPoint, endPoint }) => {
      line.start.copy(startPoint)
      line.end.copy(endPoint)

      line.closestPointToPoint(input, false, target)
      return target.distanceToSquared(input) < POINT_ON_WALL_THRESHOLD
    })

    return {
      withinBounds: isPointInPolygon([x, y], edges) || !!onWall,
      onWall,
    }
  }
}

function elevationPointAdjuster<T>(act: (payload: T) => (dispatch: AppDispatch, getState: AppGetState) => void) {
  return (payload: T) => (dispatch: AppDispatch, getState: AppGetState) => {
    const { segments: oldSegmentsObject } = getState().objects.present
    const oldSegments = Object.values(oldSegmentsObject)

    dispatch(act(payload))

    const { elevationPoints, segments: newSegments } = getState().objects.present
    const pointInFacility = facilityBoundsChecker(newSegments)

    const updatePoints: ElevationPoint[] = []

    for (const point of Object.values(elevationPoints)) {
      const { onWall } = pointInFacility(vectorUIToModel(point.position))
      if (onWall === undefined) {
        continue
      }

      const oldSegment = oldSegments.find(segment => segment.id === onWall.id)
      if (oldSegment === undefined) {
        continue
      }

      const deltaHeight = onWall.height - oldSegment.height
      updatePoints.push({
        ...point,
        position: {
          ...point.position,
          z: point.position.z + modelToUI(deltaHeight),
        },
      })
    }

    for (const elevationPoint of updatePoints) {
      dispatch(updateElevationPoint({ elevationPoint }))
    }
  }
}

function elevationPointPruner<T>(act: (payload: T) => (dispatch: AppDispatch, getState: AppGetState) => void) {
  return (payload: T) => (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(act(payload))

    const { elevationPoints, segments } = getState().objects.present
    const pointInFacility = facilityBoundsChecker(segments)

    const exteriorPoints = Object.values(elevationPoints)
      .filter(point => {
        return !pointInFacility(vectorUIToModel(point.position)).withinBounds
      })
      .map(point => point.id)

    if (exteriorPoints.length >= 1) {
      dispatch(deleteObjects(exteriorPoints))
    }
  }
}

function ceilingsUpdater<T>(act: (payload: T) => (dispatch: AppDispatch, getState: AppGetState) => void) {
  return (payload: T) => (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(act(payload))

    const { segments, objects: walls, ceilings, roofs, roofSections } = getState().objects.present

    const { ceilingsToAdd, ceilingsToDelete, ceilingsToUpdate } = updateCeilings(segments, walls, ceilings)

    ceilingsToAdd.forEach(ceiling => {
      dispatch(addCeiling({ ceiling }))
    })
    ceilingsToDelete.forEach(ceiling => {
      dispatch(deleteCeiling({ ceiling }))
    })
    ceilingsToUpdate.forEach(ceiling => {
      dispatch(updateCeiling({ ceiling }))
    })

    updateRoofs(segments, walls, roofs).forEach(roof => {
      Object.values(roofSections).forEach(section => {
        const { perimeterPoints } = roof
        const { beamRotation, columnSpacing, beamSpacing } = section
        const beamOrientation = getBeamOrientation(beamRotation)
        const columnLineModels = getMountingStructurePositions(
          perimeterPoints!,
          getOrthoOrientation(beamOrientation),
          columnSpacing
        )
        const beamModels = getMountingStructurePositions(perimeterPoints!, beamOrientation, beamSpacing)
        const updatedRoofSection = { ...section, perimeterPoints, columnLineModels, beamModels }
        dispatch(updateRoofSection({ roofSection: updatedRoofSection }))
      })
      dispatch(updateRoof({ roof }))
    })
  }
}

type GlobalState = RootState

const globalIdentity = <T>(payload: T, globalState: GlobalState) => ({ payload, globalState })

const expand =
  <T>(action: (payload: T, globalState: GlobalState) => PayloadAction<T>) =>
  (payload: T) =>
  (dispatch: AppDispatch, getState: AppGetState) =>
    dispatch(action(payload, getState()))

export const loadFacility = createAction(LOAD_FACILITY, (payload: any) => ({
  payload: { ...payload, data: patchupImport(patchupRoofSections(patchupBackgroundImage(payload.data))) },
}))

export const resetFacility = createAction<void>(RESET_FACILITY)

export const updateObjectAction = createAction(UPDATE_OBJECT, globalIdentity<any>)

export const updateObject = elevationPointAdjuster(elevationPointPruner(ceilingsUpdater(expand(updateObjectAction))))

export const updateObjectsAction = createAction<PartialObject<StateObject>[]>(UPDATE_OBJECTS)

export const updateObjects = elevationPointAdjuster(elevationPointPruner(ceilingsUpdater(expand(updateObjectsAction))))

export const updateObjectsPassively = createAction<PartialObject<StateObject>[]>(UPDATE_OBJECTS_PASSIVELY)

export const updateUtilityBoxes = updateObjects

export const updateProducts = (payload: Product[]) => (dispatch: AppDispatch, _getState: AppGetState) => {
  dispatch(updateObjects(payload))
  dispatch(status.clearStatus())
}

export const addObjectAction = createAction(ADD_OBJECT, globalIdentity<{ object: DenormalizedWall }>)
export const addObject = expand(addObjectAction)

export const updateRoofAction = createAction(UPDATE_ROOF, globalIdentity<{ roof: Roof }>)
export const updateRoof = expand(updateRoofAction)

export type RoofStructure = { roofId: string; height: number; elevationPoints: Vector3Like[]; wallId: string }
export const updateRoofStructure = createAction<RoofStructure>(UPDATE_ROOF_STRUCTURE)

export const updateRoofSectionAction = createAction(UPDATE_ROOF_SECTION, globalIdentity<{ roofSection: RoofSection }>)
export const updateRoofSection = expand(updateRoofSectionAction)

export const addMountingBeam = createAction<{
  roofSectionId: string
  beamId: string
  previousBeamId: string
  beamPosition: Vector3Like
}>(ADD_MOUNTING_BEAM)
export const removeMountingBeam = createAction<{ roofSectionId: string; beamId: string }>(REMOVE_MOUNTING_BEAM)

export const updateMountingBeam = createAction<{
  roofSectionId: string
  beamId: string
  beamPosition: Vector3Like
  direction: Vector3Like
}>(UPDATE_MOUNTING_BEAM)

export const addColumnLine = createAction<{
  roofSectionId: string
  columnLineId: string
  previousColumnLineId: string
  columnLinePosition: Vector3Like
}>(ADD_COLUMN_LINE)

export const removeColumnLine = createAction<{
  roofSectionId: string
  columnLineId: string
}>(REMOVE_COLUMN_LINE)
export const updateColumnLine = createAction<{
  roofSectionId: string
  columnLineId: string
  columnLinePosition: Vector3Like
  direction: Vector3Like
}>(UPDATE_COLUMN_LINE)

export const revertRoofSections = createAction<any>(REVERT_ROOF_SECTIONS)

export const addElevationPoint = createAction<{ elevationPoint: ElevationPoint }>(ADD_ELEVATION_POINT)

export const updateElevationPointAction = createAction(
  UPDATE_ELEVATION_POINT,
  globalIdentity<{ elevationPoint: ElevationPoint }>
)
export const updateElevationPoint = expand(updateElevationPointAction)

export const addElevationLine = createAction<{ elevationLine: ElevationLine; elevationPoints: ElevationPoint[] }>(
  ADD_ELEVATION_LINE
)

export const updateElevationLineAction = createAction(
  UPDATE_ELEVATION_LINE,
  globalIdentity<{ elevationLine: ElevationLine }>
)
export const updateElevationLine = expand(updateElevationLineAction)

export const updateWallAction = createAction(UPDATE_WALL, globalIdentity<{ wall: PartialObject<Wall> }>)
export const updateWall = elevationPointAdjuster(elevationPointPruner(ceilingsUpdater(expand(updateWallAction))))

export const updateSegmentsAction = createAction(
  UPDATE_SEGMENTS,
  globalIdentity<{ ids: string[]; updates: Partial<WallSegment> }>
)
export const updateSegments = elevationPointAdjuster(
  elevationPointPruner(ceilingsUpdater(expand(updateSegmentsAction)))
)

export const deleteObjectsAction = createAction(DELETE_OBJECTS, (payload: string[], globalState: GlobalState) => ({
  payload,
  globalState,
}))

export const deleteObjects = (payload: string[]) => (dispatch: AppDispatch, getState: AppGetState) =>
  dispatch(deleteObjectsAction(payload, getState()))

export const duplicateObjectsAction = createAction(DUPLICATE_OBJECTS, globalIdentity<any>)
export const duplicateObjects = expand(duplicateObjectsAction)

export const addDoor = createAction<{ door: Door }>(ADD_DOOR)

export const updateDoor = createAction<{ door: PartialObject<Door> }>(UPDATE_DOOR)

export const distributeDoors = createAction<{ models: Door[] }>(DISTRIBUTE_DOORS)

export const addUtilityBoxAction = createAction(ADD_UTILITY_BOX, globalIdentity<{ utilityBox: UtilityBox }>)
export const addUtilityBox = expand(addUtilityBoxAction)

export const updateUtilityBox = createAction<{ utilityBox: PartialObject<UtilityBox> }>(UPDATE_UTILITY_BOX)

export const addProductAction = createAction(ADD_PRODUCT, (product: Omit<Product, 'id'>, globalState: GlobalState) => ({
  payload: {
    product: {
      ...product,
      id: guid(),
    } as Product,
  },
  globalState,
}))
export const addProduct =
  ({ product }: { product: Omit<Product, 'id'> }) =>
  (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(addProductAction(product, getState()))
    dispatch(status.clearStatus())
  }

export const distributeProductsAction = createAction(DISTRIBUTE_PRODUCTS, globalIdentity<{ models: Product[] }>)
export const distributeProducts = expand(distributeProductsAction)

export const duplicateGrid = createAction<{ objects: any[] }>(DUPLICATE_GRID)

export const updateProductAction = createAction(UPDATE_PRODUCT, globalIdentity<any>)
export const updateProduct = expand(updateProductAction)

export const addObstructionAction = createAction(ADD_OBSTRUCTION, globalIdentity<{ obstruction: Obstruction }>)
export const addObstruction = expand(addObstructionAction)

export const distributeObstructionsAction = createAction(
  DISTRIBUTE_OBSTRUCTIONS,
  globalIdentity<{ models: Obstruction[] }>
)
export const distributeObstructions = expand(distributeObstructionsAction)

export const updateObstructionAction = createAction(
  UPDATE_OBSTRUCTION,
  globalIdentity<{ obstruction: PartialObject<Obstruction> }>
)
export const updateObstruction = expand(updateObstructionAction)

export const addComfortZone = createAction<{ comfortZone: ComfortZone }>(ADD_COMFORT_ZONE)
export const updateComfortZone = createAction<{ comfortZone: PartialObject<ComfortZone> }>(UPDATE_COMFORT_ZONE)

export const addDimensionAction = createAction(ADD_DIMENSION, globalIdentity<{ dimension: Dimension }>)
export const addDimension = expand(addDimensionAction)

export const updateDimension = createAction<{ dimension: PartialObject<Dimension> }>(UPDATE_DIMENSION)

export const addCeilingAction = createAction(ADD_CEILING, globalIdentity<{ ceiling: Ceiling }>)
export const addCeiling = expand(addCeilingAction)

export const updateCeilingAction = createAction(UPDATE_CEILING, globalIdentity<{ ceiling: Ceiling }>)
export const updateCeiling = expand(updateCeilingAction)

export const deleteCeilingAction = createAction(DELETE_CEILING, globalIdentity<{ ceiling: Ceiling }>)
export const deleteCeiling = expand(deleteCeilingAction)

export const requestAirflow = createAction(REQUEST_AIRFLOW)
export const receiveAirflow = createAction<Airflow[]>(RECEIVE_AIRFLOW)
export const updateAirflowStatus = createAction<any>(UPDATE_AIRFLOW_STATUS)

export const setAirflowLayer = createAction<any>(SET_AIRFLOW_LAYER)
export const requestHeatMap = createAction<void>(REQUEST_HEAT_MAP)
export const receiveHeatMap = createAction<any>(RECEIVE_HEAT_MAP)
export const updateHeatMapStatus = createAction<any>(UPDATE_HEAT_MAP_STATUS)
export const setHeatMapLayer = createAction<any>(SET_HEAT_MAP_LAYER)
export const addBackgroundImage = createAction<{ backgroundImage: { BACKGROUND_IMAGE: BackgroundImage } }>(
  ADD_BACKGROUND_IMAGE
)
export const deleteBackgroundImage = createAction<void>(DELETE_BACKGROUND_IMAGE)
export const updateBackgroundImage = createAction<{ backgroundImage: Partial<BackgroundImage> }>(
  UPDATE_BACKGROUND_IMAGE
)

export const addMetadataImage = createAction<any>(ADD_METADATA_IMAGE)
